import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Navigation = ({ onCTAClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownTimeoutRef = React.useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = (itemLabel) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(itemLabel);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 300);
  };

  const navItems = [
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing', href: '#pricing-example' },
    { 
      label: 'Resources', 
      href: '#resources',
      dropdown: [
        { label: 'ROI Calculator', href: '#roi-calculator' },
        { label: 'FAQ', href: '#faq' },
        { label: 'Why Not Hire Devs?', href: '#comparison' }
      ]
    },
    { label: 'Contact', href: '#contact' }
  ];;

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    
    if (href === '#contact') {
      onCTAClick();
    } else if (href === '#resources') {
      // Don't navigate, just toggle dropdown
      return;
    } else {
      const element = document.querySelector(href);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-4' 
          : 'bg-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 cursor-pointer"
          >
            <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Top layer - Green */}
              <rect x="8" y="8" width="20" height="6" rx="1.5" fill="#7AC943"/>
              {/* Middle layer - Blue */}
              <rect x="6" y="16" width="24" height="6" rx="1.5" fill="#1F6FB2"/>
              {/* Bottom layer - Light Blue */}
              <rect x="4" y="24" width="28" height="6" rx="1.5" fill="#5BA3D9"/>
            </svg>
            <div>
              <div className={`font-bold text-lg leading-tight ${isScrolled ? 'text-[#7AC943]' : 'text-[#7AC943]'}`}>
                OwnStack
              </div>
            </div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              item.dropdown ? (
                <div 
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`font-medium transition-colors duration-200 hover:text-[#7AC943] flex items-center gap-1 ${
                      isScrolled ? 'text-[#0E3A5F]' : 'text-white'
                    }`}
                  >
                    {item.label}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  
                  {openDropdown === item.label && (
                    <div 
                      className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                      onMouseEnter={() => handleMouseEnter(item.label)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {item.dropdown.map((subItem) => (
                        <a
                          key={subItem.label}
                          href={subItem.href}
                          onClick={(e) => handleNavClick(e, subItem.href)}
                          className="block px-4 py-3 text-[#0E3A5F] hover:bg-[#7AC943]/10 hover:text-[#7AC943] transition-colors duration-200"
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`font-medium transition-colors duration-200 hover:text-[#7AC943] ${
                    isScrolled ? 'text-[#0E3A5F]' : 'text-white'
                  }`}
                >
                  {item.label}
                </a>
              )
            ))}
            <Button
              onClick={onCTAClick}
              className={`${
                isScrolled 
                  ? 'bg-[#7AC943] hover:bg-[#6AB833]' 
                  : 'bg-white hover:bg-gray-100 text-[#1F6FB2]'
              } px-6 py-2 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg`}
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`h-6 w-6 ${isScrolled ? 'text-[#0E3A5F]' : 'text-white'}`} />
            ) : (
              <Menu className={`h-6 w-6 ${isScrolled ? 'text-[#0E3A5F]' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 py-4 bg-white rounded-lg shadow-xl border border-gray-100">
            <div className="flex flex-col space-y-4 px-4">
              {navItems.map((item) => (
                item.dropdown ? (
                  <div key={item.label}>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                      className="w-full text-left text-[#0E3A5F] font-medium hover:text-[#7AC943] transition-colors duration-200 py-2 flex items-center justify-between"
                    >
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === item.label && (
                      <div className="pl-4 space-y-2 mt-2">
                        {item.dropdown.map((subItem) => (
                          <a
                            key={subItem.label}
                            href={subItem.href}
                            onClick={(e) => handleNavClick(e, subItem.href)}
                            className="block text-gray-600 hover:text-[#7AC943] transition-colors duration-200 py-2"
                          >
                            {subItem.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="text-[#0E3A5F] font-medium hover:text-[#7AC943] transition-colors duration-200 py-2"
                  >
                    {item.label}
                  </a>
                )
              ))}
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onCTAClick();
                }}
                className="bg-[#7AC943] hover:bg-[#6AB833] text-white w-full py-3 rounded-lg font-semibold"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
